import { useState } from "react";
import { motion } from "framer-motion";
import { Calendar } from "lucide-react";
import { Badge, springs } from "@/components/ui";
import { TagBadge } from "@/components/ui/Tag/TagBadge";
import type { UIAudit } from "@/packages/types";
import { useTranslation } from "@/packages/utils";
import { AUDIT_STATUS_CONFIG, formatDeadline } from "./constants";

// ─── Deadline helper ─────────────────────────────────────────────────────────

function computeDeadline(deadline: number) {
  const now = Date.now();
  const daysRemaining = Math.ceil((deadline - now) / 86_400_000);
  const isOverdue = deadline < now;
  const isUrgent = !isOverdue && daysRemaining <= 7;
  return { isOverdue, daysRemaining, isUrgent };
}

// ─── Progress color helper ───────────────────────────────────────────────────

function getProgressColor(percentage: number) {
  if (percentage === 100) return "#22c55e";
  if (percentage >= 50) return "#E8862A";
  return "#a1a1a6";
}

// ─── Component ───────────────────────────────────────────────────────────────

interface AuditCardProps {
  audit: UIAudit;
  onClick?: (id: string) => void;
}

export function AuditCard({ audit, onClick }: AuditCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useTranslation(["audits"]);

  const statusConfig = AUDIT_STATUS_CONFIG[audit.status];
  const { isOverdue, daysRemaining, isUrgent } = computeDeadline(
    audit.deadline,
  );

  const percentage =
    audit.progress.total > 0
      ? Math.round((audit.progress.completed / audit.progress.total) * 100)
      : 0;

  const progressColor = getProgressColor(percentage);

  const tags = audit.tags ?? [];
  const visibleTags = tags.slice(0, 3);
  const overflowCount = tags.length - 3;

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.98 }}
      transition={springs.smooth}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={() => onClick?.(audit.id)}
      className="group relative flex flex-col items-stretch rounded-card bg-card text-left cursor-pointer overflow-hidden border border-transparent hover:border-border/60 w-full"
      style={{ padding: 0 }}
    >
      {/* ── Accent left bar — slides in on hover ── */}
      <motion.div
        className="absolute left-0 top-0 bottom-0 w-[3px] bg-accent rounded-l-card"
        initial={{ scaleY: 0 }}
        animate={{ scaleY: isHovered ? 1 : 0 }}
        transition={springs.snappy}
        style={{ originY: 0.5 }}
      />

      {/* ── Hover glow ── */}
      <motion.div
        className="absolute inset-0 rounded-card pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.2 }}
        style={{
          boxShadow:
            "0 4px 24px -4px rgba(232, 134, 42, 0.1), 0 2px 8px -2px rgba(0,0,0,0.06)",
        }}
      />

      <div className="p-card flex flex-col flex-1 gap-2.5">
        {/* ── Header: status badge + deadline urgency ── */}
        <div className="flex items-center gap-2">
          <Badge
            color={{ bg: statusConfig.badgeBg, text: statusConfig.badgeText }}
          >
            {t(`audits:status.${audit.status}`)}
          </Badge>

          {isOverdue && (
            <span className="text-[10px] font-semibold text-red-500">
              Overdue
            </span>
          )}
          {isUrgent && (
            <span className="text-[10px] font-semibold text-amber-500">
              {daysRemaining}d left
            </span>
          )}
        </div>

        {/* ── Code (monospace) + truncated title ── */}
        <div className="min-w-0">
          <p className="text-[11px] text-muted font-mono leading-none mb-1">
            {audit.code}
          </p>
          <h3 className="text-sm font-semibold text-primary leading-snug truncate">
            {audit.displayName}
          </h3>
        </div>

        {/* ── 2-line clamped description ── */}
        {audit.description && (
          <p className="text-xs text-secondary leading-relaxed line-clamp-3 min-h-[4rem]">
            {audit.description}
          </p>
        )}

        {/* ── Animated progress bar with percentage ── */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1 bg-border/50 rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${percentage}%` }}
              transition={{ ...springs.smooth, delay: 0.3 }}
              style={{ backgroundColor: progressColor }}
            />
          </div>
          <span
            className="text-[10px] font-medium shrink-0"
            style={{ color: progressColor }}
          >
            {percentage}%
          </span>
        </div>

        {/* ── Footer: tags + calendar date ── */}
        <div className="flex items-center gap-2 flex-wrap mt-auto pt-0.5">
          {/* Tag pills (max 3 + overflow) */}
          {visibleTags.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              {visibleTags.map((tag) => (
                <TagBadge
                  key={tag.id}
                  name={tag.name}
                  color={tag.color ?? "#888"}
                />
              ))}
              {overflowCount > 0 && (
                <span className="text-[10px] text-muted font-medium">
                  +{overflowCount}
                </span>
              )}
            </div>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Calendar icon + deadline date */}
          <span
            className={`flex items-center gap-1 text-[11px] shrink-0 ${
              isOverdue ? "text-red-500" : "text-secondary"
            }`}
          >
            <Calendar className="h-3 w-3" />
            {formatDeadline(audit.deadline)}
          </span>
        </div>
      </div>
    </motion.button>
  );
}
